import React from 'react'
import { useState, useEffect, useRef } from 'react'
import { Loading, RenderWhenVisible } from '@marketing-milk/frontend'
import { AdCreative } from '@marketing-milk/interfaces'

import { CreativeCard } from 'pages/business/creatives/CreativeCard/CreativeCard'
import { fuzzySearch } from 'components/DataTable/helpers/search.helpers'

import { Alert } from '@material-ui/lab'
import { creativeLetter } from 'app/util'

export type Props = {
  creatives?: AdCreative[]
  selectedCreatives: AdCreative[]
  selectCreative: (creative: AdCreative) => void
  fetchNextPage?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  isFetching?: boolean
}

export const AdCreativeList = ({
  creatives,
  selectedCreatives,
  selectCreative,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isFetching,
}: Props) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncing, setDebouncing] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const initialSelectedIds = useRef<Set<number> | null>(null)
  if (initialSelectedIds.current === null && selectedCreatives.length > 0) {
    initialSelectedIds.current = new Set(selectedCreatives.map(c => c.id))
  }

  useEffect(() => {
    setTimeout(() => setDebouncing(false), 500)
  }, [])

  // Auto-fetch next page when the filtered creative list is empty OR when the content is too
  // short to scroll (e.g. lead-gen filtering removed most creatives from the loaded pages).
  // Without this, the infinite-scroll listener never fires and the user gets stuck.
  useEffect(() => {
    if (!fetchNextPage || !hasNextPage || isFetchingNextPage) return
    if (!creatives || debouncing) return

    // Case 1: no creatives at all after filtering – fetch immediately
    if (creatives.length === 0) {
      fetchNextPage()
      return
    }

    // Case 2: some creatives but not enough to make the container scrollable
    if (!containerRef.current) return
    const scrollParent =
      containerRef.current.closest<HTMLElement>('.overflow-y-auto') ?? document.documentElement
    if (scrollParent.scrollHeight <= scrollParent.clientHeight) {
      fetchNextPage()
    }
  }, [creatives, fetchNextPage, hasNextPage, isFetchingNextPage, debouncing])

  useEffect(() => {
    if (!fetchNextPage || !hasNextPage || !containerRef.current) return
    const scrollParent =
      containerRef.current.closest<HTMLElement>('.overflow-y-auto') ?? document.documentElement
    const onScroll = () => {
      if (isFetchingNextPage) return
      if (scrollParent.scrollTop + scrollParent.clientHeight > scrollParent.scrollHeight - 200) {
        fetchNextPage()
      }
    }
    scrollParent.addEventListener('scroll', onScroll)
    return () => scrollParent.removeEventListener('scroll', onScroll)
  }, [hasNextPage, fetchNextPage, isFetchingNextPage, debouncing])

  const getButtonText = (creative: AdCreative) => {
    const index = selectedCreatives.findIndex(c => c.id === creative.id)
    return index >= 0 ? `Creative ${creativeLetter(index)}` : undefined
  }

  return (
    <>
      <div className="flex w-full space-x-4 p-4 pb-2 items-center justify-center">
        <input
          className="bg-white w-full rounded-md border px-4 py-3 max-w-6xl"
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Filter Ads"
        />
      </div>

      {creatives && creatives.length === 0 && !isFetching && (
        <div className="max-w-5xl mx-auto">
          <Alert severity="warning">
            No creatives? Make sure that your ads have all of the required info added. Also, check
            and see whether you have set the correct optimization in your campaign. If this is a
            Lead Gen campaign, you will only see creatives that have a lead card attached.
          </Alert>
        </div>
      )}

      {creatives && !debouncing ? (
        <div ref={containerRef}>
          <div className="grid w-full justify-items-center sm:grid-cols-3 3xl:grid-cols-4 4xl:grid-cols-5 sm:gap-10">
            {(fuzzySearch(creatives, searchTerm) as AdCreative[])
              .sort((a, b) => {
                const ids = initialSelectedIds.current
                if (!ids) return 0
                const aSelected = ids.has(a.id)
                const bSelected = ids.has(b.id)
                if (aSelected && !bSelected) return -1
                if (!aSelected && bSelected) return 1
                return 0
              })
              .map(creative => (
                <RenderWhenVisible key={creative.id}>
                  <CreativeCard
                    tools={false}
                    creative={creative}
                    onSelect={selectCreative}
                    selectedText={getButtonText(creative)}
                    pillDisplayed={'format'}
                  />
                </RenderWhenVisible>
              ))}
          </div>
        </div>
      ) : (
        <Loading />
      )}
    </>
  )
}
